import { useCallback, useEffect, useState } from 'react';
import { IndexedStrings } from '../../components/SelectableTable/SelectableTable.types';
import { IPageInfo } from '../../models/page.types';
import {
  AdvancedFilterBodyAssessment,
  ExamTaskGroupSampleSolution,
  SampleAssessmentSortBy,
} from './SampleAssessment.types';
import FilterStateGroup from '../../models/filterStateGroup.types';
import { AdvanceFilter, PointsFilterType } from '../Assessment/Modals/AdvancedFilter/AdvanceFilter.types';
import { AxiosError, AxiosResponse } from 'axios';
import { ExamTaskGroupSampleSolutionsResponse } from '../../services/scoringModel/scoringModel.types';
import _ from 'lodash';
import { getErrorPageUrl } from '../../utils/routerUrls';
import { ExaminersList } from '../../components/ExaminersStructure/ExaminersStructure.types';
import { DashboardTile } from '../Dashboard/Dashboard.types';
import { useLayoutContext } from '../../Context/Layout.context';
import { getExamTaskSampleSolutionsForExaminer } from '../../services/scoringModel/scoringModel.service';
import { useHistory } from 'react-router-dom';
import {
  AcceptanceStatuses,
  Alerts,
  getSortByPropertyNameFromColumnId,
  Proceses,
  Progress,
} from '../SampleAssessmentRepo/Helpers/functionsHelpers';
import { User } from 'oidc-client';
import { ExaminerFunction } from '../Assessment/Assessment.types';
import { getFormattedDateRangeValue } from '../../utils/dateFunctionUtils';
import { filtersActions } from '../../reducers/filtersReducer/filtersSlice';
import { useDispatch, useSelector } from 'react-redux';
import { DataInfoType, defaultSampleAssessmentFilter } from '../../reducers/filtersReducer/filterSlice.types';
import { RootState } from '../../store';

const getCheckedCriterionsFromCache = (advancedFilterBodyArray: AdvancedFilterBodyAssessment[]): string[] => {
  if (!advancedFilterBodyArray) return [];
  const res: string[] = [];
  for (let i = 0; i < advancedFilterBodyArray.length; i++) {
    const schemas = advancedFilterBodyArray[i].examScoringSchemas;
    schemas.map((val) => res.push(val.id));
  }
  return res;
};

export interface UseSampleAssessmentTableDataProps {
  examiner: ExaminersList | undefined;
  assessmentSession: DashboardTile;
  auth: User;
  userFunction: ExaminerFunction;
}

export const useSampleAssessmentTableData = ({
  examiner,
  assessmentSession,
  auth,
  userFunction,
}: UseSampleAssessmentTableDataProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { setIsLoader } = useLayoutContext();

  const dataInfoType = DataInfoType.SampleAssessment;
  const sampleAssessmentDataInfo = useSelector((state: RootState) => state.filters[dataInfoType]);

  const [filter, setFilter] = useState<IndexedStrings>(sampleAssessmentDataInfo.filterData.filter);
  const [isValidFilter, setIsValidFilter] = useState<boolean>(true);
  const [examTaskGroupSolutionsData, setExamTaskGroupSolutionsData] = useState<ExamTaskGroupSampleSolution[]>([]);
  const [sortByColumnId, setSortByColumnId] = useState<SampleAssessmentSortBy>(
    sampleAssessmentDataInfo.filterData.sort.sortByColumnId,
  );
  const [isDescending, setIsDescending] = useState<boolean>(sampleAssessmentDataInfo.filterData.sort.isDescending);
  const abortController = new AbortController();
  const { signal } = abortController;
  const [advancedFilter, setAdvancedFilter] = useState<AdvanceFilter | null>(
    sampleAssessmentDataInfo.filterData.advancedFilter,
  );
  const [advancedFilterBody, setAdvancedFilterBody] = useState<AdvancedFilterBodyAssessment[]>(
    sampleAssessmentDataInfo.filterData.advancedFilterBody,
  );
  const [checkedTasks, setCheckedTasks] = useState<string[]>(
    sampleAssessmentDataInfo.filterData.advancedFilterBody.map((x) => x.id),
  );
  const [checkedCriterions, setCheckedCriterions] = useState<string[]>(
    getCheckedCriterionsFromCache(sampleAssessmentDataInfo.filterData.advancedFilterBody),
  );
  const [filterState, setFilterState] = useState<FilterStateGroup[]>([]);
  const [isResetAdvancedFilter, setIsResetAdvancedFilter] = useState<boolean>(false);
  const [advancedFilterModalOpen, setAdvancedFilterModalOpen] = useState<boolean>(false);

  const isAdvancedFilter = advancedFilter !== null && checkedTasks.length > 0 && !isResetAdvancedFilter;

  const validateFilterInput = (name: string, value: string | string[]): boolean => {
    if (name === 'pointSumFrom' || name === 'pointSumTo' && value.length > 0) {
      return true;
    }
    return !(typeof value === 'string' && value.length === 1);
  };
  
  const handleFilterChange = useCallback(
    (name: string, value: string | string[]) => {
      setIsValidFilter(validateFilterInput(name, value));
      if (filter[name] === value) return;

      setFilter((state) => {
        if (state[name] !== value) {
          return {
            ...state,
            [name]: value,
          };
        } else {
          return state;
        }
      });
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: {
            filter: {
              [name]: value,
            },
            pageInfo: {
              currentPage: 0,
            },
          },
        }),
      );
    },
    [setFilter, filter],
  );

  const handleNoAnswerFilterChange = () => {
    setFilter((state) => {
      return {
        ...state,
        examTaskNumbersWithNoAnswer: state.examTaskNumbersWithNoAnswer !== 'true' ? 'true' : '',
      };
    });
    dispatch(
      filtersActions.updateFilter({
        filterType: dataInfoType,
        newValues: {
          filter: {
            examTaskNumbersWithNoAnswer:
              sampleAssessmentDataInfo.filterData.filter.examTaskNumbersWithNoAnswer !== 'true' ? 'true' : '',
          },
          pageInfo: {
            currentPage: 0,
          },
        },
      }),
    );
  };

  const handleCurrentPage = (response: IPageInfo) => {
    if (response.totalCount < sampleAssessmentDataInfo.filterData.pageInfo.pageSize) {
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: {
            pageInfo: {
              ...response,
              currentPage: 0,
            },
          },
        }),
      );
    } else {
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: {
            pageInfo: {
              ...response,
            },
          },
        }),
      );
    }
  };

  const disableChangesTillResponse = () => {
    dispatch(
      filtersActions.updateFilter({
        filterType: dataInfoType,
        newValues: {
          pageInfo: {
            hasNext: false,
            hasPrevious: false,
          },
        },
      }),
    );
  };

  const refreshData = _.debounce((silent?: boolean, signal?: AbortSignal) => {
    if (examiner && assessmentSession) {
      const filters: { [key: string]: string } = {};
      Object.keys(filter).forEach((key) => {
        if (Array.isArray(filter[key])) {
          filters[key] = (filter[key] as string[]).join(',') as string;
        } else {
          filters[key] = filter[key] as string;
        }
      });
      if (!isValidFilter) return;
      setIsLoader(!silent);
      if (examiner.examinerFunctionId) disableChangesTillResponse();

      getExamTaskSampleSolutionsForExaminer(
        examiner.id,
        examiner.function.toString(),
        (examiner.examinerFunctionId || 0).toString(),
        sampleAssessmentDataInfo.filterData.pageInfo.currentPage,
        sampleAssessmentDataInfo.filterData.pageInfo.pageSize,
        signal,
        filters,
        getSortByPropertyNameFromColumnId(sortByColumnId),
        isDescending,
        advancedFilterBody,
      )
        .then((res: AxiosResponse<ExamTaskGroupSampleSolutionsResponse>) => {
          setExamTaskGroupSolutionsData(res.data.list);
          handleCurrentPage(res.data.pageInfo);
          setIsLoader(false);
          if (advancedFilterBody) {
            setAdvancedFilterModalOpen(false);
          }
        })
        .catch((e: AxiosError) => {
          console.warn({ e });
          setIsLoader(false);

          if (e?.message !== 'canceled') {
            history.push(getErrorPageUrl(e?.code ?? 500, 'sampleExaminer'));
          }
        });
    }
  }, 500);

  const handleReload = () => {
    refreshData(true);
  };

  useEffect(() => {
    refreshData(true, signal);
  }, [isDescending, sortByColumnId]);

  const getAssessment = useCallback(
    (_page?: number, _newPageSize?: number) => {
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: {
            pageInfo: {
              currentPage: _page ? _page : 0,
              pageSize: _newPageSize ? _newPageSize : sampleAssessmentDataInfo.filterData.pageInfo.pageSize,
            },
          },
        }),
      );
    },
    [sampleAssessmentDataInfo.filterData.pageInfo],
  );

  useEffect(() => {
    if (!examiner) return;
    if (!auth.scope.includes('assessmentSession')) return;

    refreshData(false, signal);
    return () => {
      abortController.abort();
    };
  }, [
    examiner?.id,
    sampleAssessmentDataInfo.filterData.pageInfo.currentPage,
    sampleAssessmentDataInfo.filterData.pageInfo.pageSize,
    filter,
    auth.scope,
    userFunction,
    advancedFilterBody,
  ]);

  const handleAdvancedFilterChange = (filter: AdvanceFilter | null) => {
    setAdvancedFilter(filter);
    dispatch(
      filtersActions.updateFilter({
        filterType: dataInfoType,
        newValues: {
          advancedFilter: filter,
          pageInfo: {
            currentPage: 0,
          },
        },
      }),
    );
    if (filter) {
      const filterCopy = JSON.parse(JSON.stringify(filter));
      const tasksPoints: AdvancedFilterBodyAssessment[] = [];
      filterCopy?.examTasks.map((task: any) => {
        if (checkedTasks.includes(task.id)) {
          tasksPoints.push({
            id: task.id,
            exactPoints: task.pointsFilterType === PointsFilterType.exactPonts ? parseInt(task.exactPoints) : null,
            pointsFrom: task.pointsFilterType === PointsFilterType.rangePonts ? parseInt(task.pointsFrom) : null,
            pointsTo: task.pointsFilterType === PointsFilterType.rangePonts ? parseInt(task.pointsTo) : null,
            noAnswer:
              task.noAnswer === 0 || task.pointsFilterType === PointsFilterType.rangePonts || task.exactPoints
                ? null
                : task.noAnswer === 1
                ? true
                : false,
            examScoringSchemas: [],
          });
          task.examScoringSchemas.map((criterion: any) => {
            if (checkedCriterions.includes(criterion.id)) {
              tasksPoints[tasksPoints.length - 1].examScoringSchemas.push({
                id: criterion.id,
                exactPoints:
                  criterion.pointsFilterType === PointsFilterType.exactPonts ? parseInt(criterion.exactPoints) : null,
                pointsFrom:
                  criterion.pointsFilterType === PointsFilterType.rangePonts ? parseInt(criterion.pointsFrom) : null,
                pointsTo:
                  criterion.pointsFilterType === PointsFilterType.rangePonts ? parseInt(criterion.pointsTo) : null,
              });
            }
          });
        }
      });
      setAdvancedFilterBody(tasksPoints);
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: {
            advancedFilterBody: tasksPoints,
          },
        }),
      );
    }
  };

  useEffect(() => {
    const state: FilterStateGroup[] = [];
    if (!filter) return;
    if (Object.values(filter).some((e) => e?.length !== 0)) {
      state.push({
        name: '',
        checkedItems: [],
      });
    }
    if (filter.processType.length > 0) {
      state.push({
        name: 'Proces:',
        checkedItems: [...filter.processType].map((item) => Proceses.filter((e) => e.id === item)[0].label),
      });
    }
    if (filter.acceptanceStatus.length > 0) {
      state.push({
        name: 'Status:',
        checkedItems: [...filter.acceptanceStatus].map(
          (item) => AcceptanceStatuses.filter((e) => e.id === item)[0].label,
        ),
      });
    }
    if (filter.pointSumFrom.length > 0) {
      state.push({
        name: 'Suma pkt. od:',
        checkedItems: [[...filter.pointSumFrom].join('').toString()],
      });
    }
    if (filter.pointSumTo.length > 0) {
      state.push({
        name: 'Suma pkt. do:',
        checkedItems: [[...filter.pointSumTo].join('').toString()],
      });
    }
    if (filter.examTaskNumbersWithNoAnswer.length > 0) {
      state.push({
        name: 'Suma pkt.:',
        checkedItems: [...['Brak odpowiedzi']],
      });
    }
    if (filter.progress.length > 0) {
      state.push({
        name: 'Postęp:',
        checkedItems: [...filter.progress].map((item) => Progress.filter((e) => e.id === item)[0].label),
      });
    }
    if (filter.alerts.length > 0) {
      state.push({
        name: 'Monit:',
        checkedItems: [...filter.alerts].map((item) => Alerts.filter((e) => e.id === item)[0].label),
      });
    }
    if (filter.updateDateFrom.length > 0) {
      state.push({
        name: 'Aktualizacja:',
        checkedItems: [getFormattedDateRangeValue(filter.updateDateFrom.toString(), filter.updateDateTo.toString())],
      });
    }
    setFilterState(state);
  }, [filter]);

  const handleUncheckProcesItem = useCallback(
    (value: string) => {
      const procesFilter = [...filter.processType];
      const valueId = Proceses.filter((e) => e.label === value)[0].id;
      const val = procesFilter.some((f) => f === valueId)
        ? procesFilter.filter((f) => f !== valueId)
        : [...procesFilter, valueId];
      setFilter((state) => ({
        ...state,
        processType: val,
      }));
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: {
            filter: {
              processType: val,
            },
            pageInfo: {
              currentPage: 0,
            },
          },
        }),
      );
    },
    [filter],
  );

  const handleUncheckProgessItem = useCallback(
    (value: string) => {
      const progresFilter = [...filter.progress];
      const valueId = Progress.filter((e) => e.label === value)[0].id;
      const val = progresFilter.some((f) => f === valueId)
        ? progresFilter.filter((f) => f !== valueId)
        : [...progresFilter, valueId];
      setFilter((state) => ({
        ...state,
        progress: val,
      }));
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: {
            filter: {
              progress: val,
            },
            pageInfo: {
              currentPage: 0,
            },
          },
        }),
      );
    },
    [filter],
  );
  const handleUncheckAlertsItem = useCallback(
    (value: string) => {
      const alertsFilter = [...filter.alerts];
      const valueId = Alerts.filter((e) => e.label === value)[0].id;
      const val = alertsFilter.some((f) => f === valueId)
        ? alertsFilter.filter((f) => f !== valueId)
        : [...alertsFilter, valueId];
      setFilter((state) => ({
        ...state,
        alerts: val,
      }));
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: {
            filter: {
              alerts: val,
            },
            pageInfo: {
              currentPage: 0,
            },
          },
        }),
      );
    },
    [filter],
  );

  const handleUncheckStatusItem = useCallback(
    (value: string) => {
      const acceptanceStatusFilter = [...filter.acceptanceStatus];
      const valueId = AcceptanceStatuses.filter((e) => e.label === value)[0].id;
      const val = acceptanceStatusFilter.some((f) => f === valueId)
        ? acceptanceStatusFilter.filter((f) => f !== valueId)
        : [...acceptanceStatusFilter, valueId];
      setFilter((state) => ({
        ...state,
        acceptanceStatus: val,
      }));
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: {
            filter: {
              acceptanceStatus: val,
            },
            pageInfo: {
              currentPage: 0,
            },
          },
        }),
      );
    },
    [filter],
  );

  const uncheckFilterItem = (item?: string) => {
    if (item) {
      const element = filterState.find((e) => e.checkedItems.find((x) => x === item));
      if (element?.name) {
        switch (element.name) {
          case 'Proces:':
            handleUncheckProcesItem(item);
            break;
          case 'Suma pkt.:':
            setFilter((state) => ({
              ...state,
              examTaskNumbersWithNoAnswer: '',
            }));
            dispatch(
              filtersActions.updateFilter({
                filterType: dataInfoType,
                newValues: {
                  filter: {
                    examTaskNumbersWithNoAnswer: '',
                  },
                  pageInfo: {
                    currentPage: 0,
                  },
                },
              }),
            );
            break;
          case 'Suma pkt. od:':
            {
              setFilter((state) => ({
                ...state,
                pointSumFrom: '',
              }));
              dispatch(
                filtersActions.updateFilter({
                  filterType: dataInfoType,
                  newValues: {
                    filter: {
                      pointSumFrom: '',
                    },
                    pageInfo: {
                      currentPage: 0,
                    },
                  },
                }),
              );
            }
            break;
          case 'Suma pkt. do:':
            {
              setFilter((state) => ({
                ...state,
                pointSumTo: '',
              }));
              dispatch(
                filtersActions.updateFilter({
                  filterType: dataInfoType,
                  newValues: {
                    filter: {
                      pointSumTo: '',
                    },
                    pageInfo: {
                      currentPage: 0,
                    },
                  },
                }),
              );
            }
            break;
          case 'Status:':
            handleUncheckStatusItem(item);
            break;

          case 'Postęp:':
            handleUncheckProgessItem(item);
            break;
          case 'Monit:':
            handleUncheckAlertsItem(item);
            break;
          case 'Aktualizacja:': {
            setFilter((state) => ({
              ...state,
              updateDateFrom: '',
              updateDateTo: '',
            }));
            dispatch(
              filtersActions.updateFilter({
                filterType: dataInfoType,
                newValues: {
                  filter: {
                    updateDateFrom: '',
                    updateDateTo: '',
                  },
                  pageInfo: {
                    currentPage: 0,
                  },
                },
              }),
            );
          }
        }
      }
    }
  };

  const uncheckAllFilters = () => {
    setFilterState([]);
    setFilter(defaultSampleAssessmentFilter);
    dispatch(filtersActions.resetFilter(dataInfoType));
  };

  const onAdvancedFilterReset = () => {
    setIsResetAdvancedFilter(true);
    setAdvancedFilter(null);
    setCheckedCriterions([]);
    setCheckedTasks([]);
    setAdvancedFilterBody([]);
    dispatch(
      filtersActions.updateFilter({
        filterType: dataInfoType,
        newValues: {
          advancedFilter: null,
          advancedFilterBody: [],
          pageInfo: {
            currentPage: 0,
          },
        },
      }),
    );
  };

  const setIsDescendingOrder = (order: boolean) => {
    setIsDescending(order);
    dispatch(
      filtersActions.updateFilter({
        filterType: dataInfoType,
        newValues: { sort: { isDescending: order } },
      }),
    );
  };

  const setSortByColumn = (column: SampleAssessmentSortBy) => {
    setSortByColumnId(column);
    dispatch(
      filtersActions.updateFilter({
        filterType: dataInfoType,
        newValues: { sort: { sortByColumnId: column } },
      }),
    );
  };

  return {
    filter,
    pageInfo: sampleAssessmentDataInfo.filterData.pageInfo,
    examTaskGroupSolutionsData,
    sortByColumnId,
    setSortByColumnId: setSortByColumn,
    isDescending,
    setIsDescending: setIsDescendingOrder,
    advancedFilter,
    setAdvancedFilter,
    setAdvancedFilterBody,
    checkedTasks,
    setCheckedTasks,
    checkedCriterions,
    setCheckedCriterions,
    filterState,
    handleFilterChange,
    handleNoAnswerFilterChange,
    setIsResetAdvancedFilter,
    setAdvancedFilterModalOpen,
    disableChangesTillResponse,
    signal,
    refreshData,
    handleReload,
    getAssessment,
    isAdvancedFilter,
    uncheckFilterItem,
    uncheckAllFilters,
    advancedFilterModalOpen,
    isResetAdvancedFilter,
    handleAdvancedFilterChange,
    onAdvancedFilterReset,
    advancedFilterBody,
  };
};
